import { BaseController, IBaseScope } from "@app/views/base.view";
import { CrudController } from "@app/views/crud.view";
import { events, Utils } from "@tools";
import { NgView } from "@app/di/decorators";
import { Token } from "@app/di/token";
import { IKbUserRegister, eEnvironment } from "@models";
import { AuthService } from "@app/services/auth.service";
import { IRootScope } from "@models";

export interface ILogin {
    email?: string;
    password?: string;
    rememberMe?: boolean;
}

export interface IForgotPassword {
    email?: string;
}

export enum eLoginMode {
    login,
    register,
    thanks,
    forgotPassword,
    forgotPasswordSubmitted
}

export interface ILoginScope extends IBaseScope {
    loginModel: ILogin;
    userModel: IKbUserRegister;
    forgotPasswordModel: IForgotPassword;
    registrationModel: any;
    validation: any;
    ssoEnabled: boolean;
    ssoSignIn: () => void;
    showAltLogin: () => void;

    login: () => void;
    loginVisible: boolean;
    loginError: string;
    altLogin: boolean;

    submitForgotPassword: () => ng.IPromise<any>;

    mode: eLoginMode;
    setMode: (mode: eLoginMode) => void;
    register: () => ng.IPromise<any>;
}

@NgView({
    token: Token.LoginView,
    dependencies: [
        Token.$Scope,
        Token.$Http,
        Token.AuthService,
        Token.$RootScope,
        Token.$Timeout,
        Token.$Window,
        Token.$Location
    ]
})
export class LoginController extends BaseController {
    constructor(
        private $scope: ILoginScope,
        private $http: ng.IHttpService,
        private authService: AuthService,
        private $rootScope: IRootScope,
        private $timeout: ng.ITimeoutService,
        private $window: ng.IWindowService,
        private $location: ng.ILocationService
    ) {

        super();

        $rootScope.setTitle(loc.login);

        $scope.mode = eLoginMode.login;
        $scope.loginModel = {};
        $scope.userModel = {};
        $scope.forgotPasswordModel = {};
        $scope.registrationModel = {};
        $scope.ssoEnabled = $rootScope.context.ssoEnabled;

        $scope.login = () => {
            authService.login(
                $scope.loginModel.email,
                $scope.loginModel.password,
                $scope.loginModel.rememberMe
            ).finally(() => {
                $scope.loginError = authService.loginError;
            });
        };
        $scope.loginVisible = false;

        $scope.setMode = mode => {
            $scope.mode = mode;

            switch (mode) {
                case eLoginMode.login:
                    $rootScope.setTitle(loc.login);
                    break;
                case eLoginMode.register:
                case eLoginMode.thanks:
                    $rootScope.setTitle(loc.createaccount);
                    break;
                case eLoginMode.forgotPassword:
                case eLoginMode.forgotPasswordSubmitted:
                    $rootScope.setTitle(loc.forgotpassword);
                    break;
                default:
                    $rootScope.setTitle();
                    break;
            }
        };

        $scope.ssoSignIn = () => {
            if (Utils.isDefined($location.search().embedded)) {
                Utils.sendMessageToParent({
                    name: "loginsso",
                    data: {}
                });
            }
            else {
                $window.location.href = "/ssosignin?RedirectUrl=" + $window.location.href;
            }
        }

        $scope.showAltLogin = () => {
            $scope.altLogin = true;
        }

        let off1 = $rootScope.$on(events.loginRequested, () => {
            if ($scope.ssoEnabled && Utils.getParameterByName($window.location.href, "autoSsoLogin")) {
                $scope.ssoSignIn();
            }
            $scope.loginVisible = true;
        });

        let off2 = $rootScope.$on(events.loginSuccessful, () => {
            this.$timeout(() => {
                // delay emptying while the fade out animation runs
                $scope.loginVisible = false;
                $scope.loginModel.email = "";
                $scope.loginModel.password = "";
            }, 500);
        });

        $scope.register = () => {
            if (!$scope.registrationModel.agreedToPrivacyPolicy) {
                $scope.validation = { agreedToPrivacyPolicy: "You must agree to the privacy policy before continuing" };
                return;
            }
            return $http.post("/api/auth/register", $scope.userModel, { tracker: $rootScope.loginTracker }).then(() => {
                $scope.mode = eLoginMode.thanks;
            }, (r: ng.IHttpResponse<any>) => {
                $scope.validation = CrudController.prototype.getValidationObject(r.data, r.status);
            });
        };

        $scope.submitForgotPassword = () => {
            return $http.post(
                "/api/auth/forgotpassword",
                $scope.forgotPasswordModel,
                { tracker: $rootScope.loginTracker }
            ).then(() => {
                $scope.mode = eLoginMode.forgotPasswordSubmitted;
            }, (r: ng.IHttpResponse<any>) => {
                $scope.validation = CrudController.prototype.getValidationObject(r.data, r.status);
            });
        };

        if (!$rootScope.context.user && (!$rootScope.context.allowAnonymousLogins || $rootScope.environment !== eEnvironment.prod) && Utils.getParameterByName($window.location.href, "autoSsoLogin")) {
            $scope.ssoSignIn();
        }

        $scope.$on("$destroy", () => {
            off1();
            off2();
        });
    }
}
